import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../config/supabase';
import { useNavigate, useLocation } from 'react-router-dom';
import { User } from '@supabase/supabase-js';

interface AuthResponse {
  user: User | null;
  error?: Error;
}

interface AuthContextType {
  user: User | null;
  signIn: (email: string, password: string) => Promise<AuthResponse>;
  signOut: () => Promise<void>;
  loading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Verificar la sesión actual y suscribirse a cambios
  useEffect(() => {
    // Configurar la persistencia de la sesión
    supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN') {
        setUser(session?.user ?? null);
        const redirectTo = location.state?.from?.pathname || '/';
        navigate(redirectTo);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        navigate('/login');
      }
    });

    // Verificar sesión actual
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setUser(session?.user ?? null);
        
        if (!session) {
          // Solo redirigir al login si no estamos ya en la página de login
          if (!location.pathname.includes('/login')) {
            navigate('/login');
          }
        }
      } catch (err) {
        console.error('Error checking session:', err);
      } finally {
        setLoading(false);
      }
    };

    checkSession();
  }, [navigate, location]);

  const updateLastActivity = async (userId: string) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ last_activity: new Date().toISOString() })
        .eq('id', userId);

      if (error) throw error;
    } catch (err) {
      console.error('Error updating last activity:', err);
    }
  };

  const logActivityStatus = async (userId: string, isOnline: boolean, reason: string) => {
    try {
      const now = new Date().toISOString();

      // Primero actualizar cualquier registro existente que esté abierto
      const { data: openLogs } = await supabase
        .from('activity_logs')
        .update({ end_time: now })
        .eq('employee_id', userId)
        .is('end_time', null);

      // Crear nuevo registro
      const { error: insertError } = await supabase
        .from('activity_logs')
        .insert({
          employee_id: userId,
          status: isOnline,
          start_time: now,
          end_time: isOnline ? null : now,
          reason: reason
        });

      if (insertError) {
        throw insertError;
      }
    } catch (error) {
      console.error('Error en logActivityStatus:', error);
    }
  };

  const signIn = async (email: string, password: string): Promise<AuthResponse> => {
    try {
      setError(null);
      setLoading(true);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // Si el inicio de sesión es exitoso
      if (data.user) {
        // 1. Actualizar estado en profiles
        const { error: profileError } = await supabase
          .from('profiles')
          .update({ is_active: true })
          .eq('id', data.user.id);

        if (profileError) {
          console.error('Error updating profile status:', profileError);
          throw profileError;
        }

        // 2. Registrar en employee_status
        const { error: statusError } = await supabase
          .from('employee_status')
          .insert({
            employee_id: data.user.id,
            status: true,
            last_status_change: new Date().toISOString()
          });

        if (statusError) {
          console.error('Error creating employee status:', statusError);
          throw statusError;
        }

        // 3. Registrar actividad
        await logActivityStatus(data.user.id, true, 'Inicio de sesión');

        // 4. Actualizar última actividad
        await updateLastActivity(data.user.id);
      }

      return { user: data.user };
    } catch (err: any) {
      setError(err.message);
      return { user: null, error: err };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setError(null);
      setLoading(true);
      
      if (user?.id) {
        // 1. Actualizar estado en profiles
        const { error: profileError } = await supabase
          .from('profiles')
          .update({ 
            is_active: false,
            has_webcam: false,
            webcam_last_checked: new Date().toISOString(),
            last_activity: new Date().toISOString()
          })
          .eq('id', user.id);

        if (profileError) {
          console.error('Error updating profile status:', profileError);
        }

        // 2. Registrar actividad
        await logActivityStatus(user.id, false, 'Cierre de sesión');
      }

      // 3. Cerrar sesión en Supabase
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

    } catch (err: any) {
      console.error('Error during sign out:', err);
      await supabase.auth.signOut();
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        signIn,
        signOut,
        loading,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
